"use client"

import { APIProvider } from '@vis.gl/react-google-maps';
import { useJsApiLoader } from '@react-google-maps/api';
import React, { PropsWithChildren } from 'react';

// Stable reference so useJsApiLoader does not re-run internal logic on every parent re-render
const LIBRARIES: ("places")[] = ["places"];

/**
 * Loads the Maps JS API once with the Places library (needed by Autocomplete)
 * and waits until it is ready before mounting the app tree.
 *
 * - Passes the same libraries to APIProvider so @vis.gl maps and
 *   @react-google-maps/api Autocomplete share one Places-capable load.
 * - If the API key is missing or the script fails, still render children so
 *   the rest of the app is not blank (maps/autocomplete simply won't work).
 */
function MapWrapper ({ children }: PropsWithChildren) {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";
    const { isLoaded, loadError } = useJsApiLoader({
        googleMapsApiKey: apiKey,
        libraries: LIBRARIES,
    });

    const mapsReady = !apiKey || isLoaded || Boolean(loadError);

    return (
        <APIProvider apiKey={apiKey} libraries={LIBRARIES}>
            {mapsReady ? children : null}
        </APIProvider>
    );
};

export default MapWrapper;
